import type { Currency } from "@/Apollo/schema";
import { decodeHtmlEntities } from "@/common/utils/html-entities.utils";
import { formatPrice } from "@/common/utils/intl.utils";
import { toContentLocale } from "@/i18n/config";

/**
 * Locale-specific <title>, meta description and details-block copy for a
 * product page (locale differentiation, phase 1).
 *
 * Search Console (15 to 17 Sept 2026) lists 824k product URLs as "crawled,
 * currently not indexed" and 122k as "Google chose a different canonical",
 * almost all of them product pages in the five non-English locales. The six
 * locale versions of a listing share photos, price and layout and differed
 * only by a machine-translated title and description, so Google collapsed
 * them onto one. Product pages earn 57% of organic clicks, so the fix has to
 * keep the dips small: only the title, the meta description and a details
 * block change here. URLs, slugs, canonicals, hreflang, redirects, JSON-LD
 * and the sold-page lifecycle are untouched.
 *
 * Everything in this file is pure so `getServerSideProps`-adjacent code and
 * the tests read one definition. Strings default from the typed maps below
 * until the Lokalise keys exist (see `PRODUCT_SEO_COPY`).
 */

export type ProductSeoLocale = "en" | "nl" | "de" | "fr" | "es" | "it";

type ProductSeoAttribute = {
  __typename?: string | null;
  title?: string | null;
};

type ProductSeoCategory = {
  title?: string | null;
  slug?: string | null;
  level?: number | null;
};

/**
 * The slice of `ProductBySlug` these builders read. Callers pass the full
 * product; array items allow `undefined` because PartialDeep fixtures do.
 */
export type ProductSeoProduct = {
  title?: string | null;
  brand_suggestion?: string | null;
  attributes?: ReadonlyArray<ProductSeoAttribute | null | undefined> | null;
  categories?: ReadonlyArray<ProductSeoCategory | null | undefined> | null;
  quality?: string | null;
  width?: number | null;
  height?: number | null;
  depth?: number | null;
  buy_now_price?: { amount?: number | null; currency?: string | null } | null;
  address?: { city?: string | null; country?: string | null } | null;
  delivery_method?: string | null;
};

export const WHOPPAH_TITLE_SUFFIX = " | Whoppah";

/** The listing title is clipped here first; see `buildProductPageTitle`. */
export const TITLE_HEAD_MAX_LENGTH = 55;

/** The shorter clip used when the whole title still runs over budget. */
export const TITLE_HEAD_SHORT_LENGTH = 45;

/** Google shows roughly this many characters of a <title>. */
export const TITLE_MAX_LENGTH = 70;

/** The description aims for 140 to 160 characters. */
export const DESCRIPTION_TARGET_LENGTH = 160;

/**
 * Above this the description is compacted by dropping clauses. A little
 * over the target is left alone: Google clips the tail, and the
 * dimensions are worth more than the last words of the trust phrase.
 */
export const DESCRIPTION_MAX_LENGTH = 175;

type LocaleCopy = Record<ProductSeoLocale, string>;

type ProductSeoCopyEntry = { key: string; defaults: LocaleCopy };

/**
 * Every UI string this feature adds, with its Lokalise key and the six
 * defaults it falls back to until the key exists. Components read them as
 * `t(entry.key, { defaultValue: entry.defaults[locale] })`; the pure
 * builders read the defaults directly.
 */
export const PRODUCT_SEO_COPY = {
  categoryLabel: {
    key: "pdp:pdp-attribute-category",
    defaults: {
      en: "Category",
      nl: "Categorie",
      de: "Kategorie",
      fr: "Catégorie",
      es: "Categoría",
      it: "Categoria",
    },
  },
  sellerLocationLabel: {
    key: "pdp:pdp-attribute-seller-location",
    defaults: {
      en: "Seller location",
      nl: "Locatie verkoper",
      de: "Standort des Verkäufers",
      fr: "Localisation du vendeur",
      es: "Ubicación del vendedor",
      it: "Località del venditore",
    },
  },
  deliveryOptionsLabel: {
    key: "pdp:pdp-attribute-delivery-options",
    defaults: {
      en: "Delivery options",
      nl: "Bezorgopties",
      de: "Lieferoptionen",
      fr: "Options de livraison",
      es: "Opciones de entrega",
      it: "Opzioni di consegna",
    },
  },
  deliveryOptionDelivery: {
    key: "pdp:pdp-delivery-option-delivery",
    defaults: {
      en: "Delivery",
      nl: "Bezorgen",
      de: "Lieferung",
      fr: "Livraison",
      es: "Envío",
      it: "Consegna",
    },
  },
  deliveryOptionPickup: {
    key: "pdp:pdp-delivery-option-pickup",
    defaults: {
      en: "Pickup",
      nl: "Ophalen",
      de: "Abholung",
      fr: "Retrait",
      es: "Recogida",
      it: "Ritiro",
    },
  },
  deliveryOptionDeliveryOrPickup: {
    key: "pdp:pdp-delivery-option-delivery-or-pickup",
    defaults: {
      en: "Delivery or pickup",
      nl: "Bezorgen of ophalen",
      de: "Lieferung oder Abholung",
      fr: "Livraison ou retrait",
      es: "Envío o recogida",
      it: "Consegna o ritiro",
    },
  },
} satisfies Record<string, ProductSeoCopyEntry>;

/** The one word that tells a searcher this is pre-owned, per locale. */
const INTENT_WORD: LocaleCopy = {
  en: "second-hand",
  nl: "tweedehands",
  de: "gebraucht",
  fr: "d'occasion",
  es: "de segunda mano",
  it: "usato",
};

/** "design" is the same word in all six site languages. */
const DESIGN_WORD: LocaleCopy = {
  en: "design",
  nl: "design",
  de: "design",
  fr: "design",
  es: "design",
  it: "design",
};

/**
 * `ProductQuality` (GOOD, GREAT, PERFECT) as the `common:condition-*`
 * translations read on 2026-09-17, so the server-built description says
 * the same thing the specs panel does.
 */
const CONDITION_COPY: Record<string, LocaleCopy> = {
  GOOD: {
    en: "Good",
    nl: "Goed",
    de: "Gut",
    fr: "Bon",
    es: "Bien",
    it: "Buono",
  },
  GREAT: {
    en: "Very good",
    nl: "Heel goed",
    de: "Sehr gut",
    fr: "Très bon",
    es: "Muy buena",
    it: "Molto buono",
  },
  PERFECT: {
    en: "Excellent",
    nl: "Uitstekend",
    de: "Ausgezeichnet",
    fr: "Excellent",
    es: "Excelente",
    it: "Eccellente",
  },
};

const PRICE_LABEL: LocaleCopy = {
  en: "Price",
  nl: "Prijs",
  de: "Preis",
  fr: "Prix",
  es: "Precio",
  it: "Prezzo",
};

/** "{sentence} {city}." with the city interpolated. */
const SELLER_SENTENCE: LocaleCopy = {
  en: "Sold by a seller in {city}.",
  nl: "Aangeboden door een verkoper in {city}.",
  de: "Angeboten von einem Verkäufer in {city}.",
  fr: "Proposé par un vendeur à {city}.",
  es: "Ofrecido por un vendedor en {city}.",
  it: "Offerto da un venditore a {city}.",
};

const TRUST_PHRASE: LocaleCopy = {
  en: "Curated second-hand design on Whoppah.",
  nl: "Gecureerd tweedehands design op Whoppah.",
  de: "Kuratiertes gebrauchtes Design bei Whoppah.",
  fr: "Design d'occasion sélectionné sur Whoppah.",
  es: "Diseño de segunda mano seleccionado en Whoppah.",
  it: "Design usato selezionato su Whoppah.",
};

/**
 * The country a locale's visitors are in, so the description only names
 * the seller's country when it is somewhere else. English has no home
 * country: its visitors are international, so the country always shows.
 */
const LOCALE_HOME_COUNTRY: Partial<Record<ProductSeoLocale, string>> = {
  nl: "NL",
  de: "DE",
  fr: "FR",
  es: "ES",
  it: "IT",
};

const SUPPORTED_LOCALES: ReadonlyArray<ProductSeoLocale> = [
  "en",
  "nl",
  "de",
  "fr",
  "es",
  "it",
];

/**
 * Narrow a Next locale to one of the six; English for anything unknown.
 * `en-us` reads `en` (WHO-5766): the copy is per content language.
 */
export const toProductSeoLocale = (locale?: string | null): ProductSeoLocale =>
  SUPPORTED_LOCALES.find(
    supported => supported === toContentLocale(locale ?? undefined)
  ) ?? "en";

/**
 * Seller-written text can carry em and en dashes, which the copy rules
 * forbid in anything we emit. Runs of whitespace are collapsed too, since
 * titles are pasted from all over.
 */
const cleanText = (text?: string | null) =>
  decodeHtmlEntities(text ?? "")
    .replace(/\s*[\u2013\u2014]\s*/g, " - ")
    .replace(/\s+/g, " ")
    .trim();

const truncateAtWord = (text: string, max: number) => {
  if (text.length <= max) {
    return text;
  }

  const clipped = text.slice(0, max);
  const lastSpace = clipped.lastIndexOf(" ");

  return (lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped).trim();
};

const capitalizeFirst = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

const lowercaseFirst = (text: string) =>
  text ? text.charAt(0).toLowerCase() + text.slice(1) : text;

/**
 * Seller addresses arrive as typed, and a share of cities are shouted
 * ("PARIS", "NIEUW-VENNEP"). Only a string with no lowercase letter at all is
 * touched, so "Den Haag", "'s-Hertogenbosch" and "Bad Homburg v. d. Hohe"
 * keep their own casing; a shouted one becomes title case per word, across
 * spaces, hyphens and apostrophes.
 */
export const titleCaseIfShouted = (text: string) => {
  if (!text || /[a-zà-öø-ÿ]/.test(text)) {
    return text;
  }

  return text
    .toLocaleLowerCase()
    .replace(
      /(^|[\s\-'’])([a-zà-öø-ÿ])/g,
      (match, boundary, letter) => `${boundary}${letter.toLocaleUpperCase()}`
    );
};

const attributeTitle = (
  product: ProductSeoProduct | null | undefined,
  typename: string
) =>
  (product?.attributes ?? [])
    .filter(attribute => attribute?.__typename === typename)
    .map(attribute => cleanText(attribute?.title))
    .find(title => title.length > 0);

/** The brand as the gateway names it, or the seller's own suggestion. */
export const resolveBrandName = (
  product: ProductSeoProduct | null | undefined
) => attributeTitle(product, "Brand") ?? cleanText(product?.brand_suggestion);

/**
 * The most specific category, localized by the gateway (`title(lang:)`).
 * The PDP fragment always selects `title`; the slug fallback only covers
 * callers whose fragment does not.
 */
export const resolveLeafCategoryTitle = (
  product: ProductSeoProduct | null | undefined
) => {
  const deepest = (product?.categories ?? [])
    .filter((category): category is ProductSeoCategory => !!category)
    .reduce<ProductSeoCategory | undefined>(
      (best, category) =>
        (category.level ?? 0) >= (best?.level ?? -1) ? category : best,
      undefined
    );

  const label =
    cleanText(deepest?.title) || cleanText(deepest?.slug?.replace(/-/g, " "));

  return label || undefined;
};

/** The localized condition label, or undefined for an unknown value. */
export const resolveConditionLabel = (
  product: ProductSeoProduct | null | undefined,
  locale: ProductSeoLocale
) => {
  const quality = product?.quality?.toUpperCase();

  if (!quality) {
    return undefined;
  }

  return CONDITION_COPY[quality]?.[locale];
};

/**
 * "80 x 90 x 85 cm" from whichever of width, height and depth are set. A
 * single value is left out: "85 cm" on its own says nothing about which
 * side it measures.
 */
export const formatDimensions = (
  product: ProductSeoProduct | null | undefined
) => {
  const sides = [product?.width, product?.height, product?.depth].filter(
    (side): side is number => typeof side === "number" && side > 0
  );

  if (sides.length < 2) {
    return undefined;
  }

  return `${sides.join(" x ")} cm`;
};

/**
 * Country name in the visitor's language. `Intl.DisplayNames` exists in
 * Node 22 and every evergreen browser; the code itself is the fallback.
 */
const countryName = (countryCode: string, locale: ProductSeoLocale) => {
  try {
    return (
      new Intl.DisplayNames([locale], { type: "region" }).of(countryCode) ??
      countryCode
    );
  } catch {
    return countryCode;
  }
};

/**
 * "Amsterdam" for a Dutch visitor, "Amsterdam, Netherlands" for anyone
 * else; see `LOCALE_HOME_COUNTRY`.
 */
export const formatSellerLocation = (
  product: ProductSeoProduct | null | undefined,
  locale: ProductSeoLocale
) => {
  const city = titleCaseIfShouted(cleanText(product?.address?.city));
  const country = product?.address?.country?.toUpperCase();

  if (!city && !country) {
    return undefined;
  }

  const showCountry = !!country && LOCALE_HOME_COUNTRY[locale] !== country;
  const parts = [city, showCountry ? countryName(country, locale) : ""].filter(
    Boolean
  );

  return parts.join(", ") || undefined;
};

/** Which copy entry describes the product's `delivery_method`, if any. */
export const resolveDeliveryOptionCopy = (
  product: ProductSeoProduct | null | undefined
): ProductSeoCopyEntry | undefined => {
  switch (product?.delivery_method?.toUpperCase()) {
    case "DELIVERY":
      return PRODUCT_SEO_COPY.deliveryOptionDelivery;
    case "PICKUP":
      return PRODUCT_SEO_COPY.deliveryOptionPickup;
    case "PICKUP_DELIVERY":
      return PRODUCT_SEO_COPY.deliveryOptionDeliveryOrPickup;
    default:
      return undefined;
  }
};

/** "Delivery or pickup" in the visitor's language, or undefined. */
export const formatDeliveryOptions = (
  product: ProductSeoProduct | null | undefined,
  locale: ProductSeoLocale
) => resolveDeliveryOptionCopy(product)?.defaults[locale];

/** The asking price the way the rest of the site prints it. */
export const formatProductPrice = (
  product: ProductSeoProduct | null | undefined,
  locale: ProductSeoLocale
) => {
  const price = product?.buy_now_price;

  if (typeof price?.amount !== "number" || price.amount <= 0) {
    return undefined;
  }

  // The gateway's `currency` is the `Currency` enum serialised as a
  // string; the SEO product type keeps it a string so fixtures stay plain.
  const formatted = formatPrice(
    {
      amount: price.amount,
      currency: (price.currency ?? undefined) as Currency | undefined,
    },
    { locale }
  );

  // Intl puts a no-break space between the sign and the number. Meta text
  // is plain prose, so it gets an ordinary space.
  return formatted ? formatted.replace(/\u00a0/g, " ") : undefined;
};

const joinTitle = (head: string, middle: string) =>
  `${head} | ${middle}${WHOPPAH_TITLE_SUFFIX}`;

/**
 * `{listing title} | {brand} {category} {intent} | Whoppah`, without the
 * price. The price used to sit in the title; it changes over the life of a
 * listing and the six locales printed the same number, which was part of
 * what made them look identical.
 *
 *  - The listing title is clipped to 55 characters at a word boundary.
 *  - The brand appears only when known and not already in the title.
 *  - The category is the localized leaf category.
 *  - With neither brand nor category the middle is "{Intent} design".
 *  - Over 70 characters: drop the brand first, then clip the title to 45.
 *    A long category plus a long intent word ("de segunda mano") can still
 *    run past 70 once the head is already short; that overrun is accepted
 *    rather than cutting the category, which is the differentiating word.
 */
export const buildProductPageTitle = (
  product: ProductSeoProduct | null | undefined,
  locale?: string | null
) => {
  const seoLocale = toProductSeoLocale(locale);
  const rawTitle = cleanText(product?.title);
  const intent = INTENT_WORD[seoLocale];

  const brand = resolveBrandName(product);
  const category = resolveLeafCategoryTitle(product);

  const brandInTitle =
    !!brand && rawTitle.toLowerCase().includes(brand.toLowerCase());
  const brandPart = brand && !brandInTitle ? brand : undefined;

  if (!brandPart && !category) {
    const middle = `${capitalizeFirst(intent)} ${DESIGN_WORD[seoLocale]}`;
    const head = truncateAtWord(rawTitle, TITLE_HEAD_MAX_LENGTH);
    const full = joinTitle(head, middle);

    if (full.length <= TITLE_MAX_LENGTH) {
      return full;
    }

    return joinTitle(truncateAtWord(rawTitle, TITLE_HEAD_SHORT_LENGTH), middle);
  }

  /*
   * Brand, category and the intent word are the words a searcher types, so
   * they are never dropped. When the whole title runs long the seller's own
   * title gives way first, trimmed at a word boundary down to the 45-char
   * clip; if it is still over 70 after that the overrun is accepted. Google
   * shows about 60 characters but matches on the whole title, so a brand
   * past the visible cut still counts, while a dropped brand cannot.
   */
  const middle = [brandPart, category, intent]
    .filter((part): part is string => !!part)
    .join(" ");
  const room = TITLE_MAX_LENGTH - joinTitle("", middle).length;
  const headMax = Math.max(
    TITLE_HEAD_SHORT_LENGTH,
    Math.min(TITLE_HEAD_MAX_LENGTH, room)
  );

  return joinTitle(truncateAtWord(rawTitle, headMax), middle);
};

/**
 * The title without " | Whoppah", for the shared `SEO` component, which
 * appends that suffix itself.
 */
export const buildProductPageTitleBase = (
  product: ProductSeoProduct | null | undefined,
  locale?: string | null
) => {
  const full = buildProductPageTitle(product, locale);

  return full.slice(0, full.length - WHOPPAH_TITLE_SUFFIX.length);
};

/**
 * Plain-text meta description of about 140 to 160 characters:
 *
 *   {brand} {category}, {condition}, {w} x {h} x {d} cm. Price {price}.
 *   Sold by a seller in {city}. Delivery or pickup.
 *   Curated second-hand design on Whoppah.
 *
 * Any clause whose data is missing is left out, so nothing dangles. With
 * neither brand nor category the first clause opens with the listing
 * title instead. When the whole runs past 160 characters the dimensions
 * go first, then the delivery clause; the trust phrase always stays.
 */
export const buildProductPageDescription = (
  product: ProductSeoProduct | null | undefined,
  locale?: string | null
) => {
  const seoLocale = toProductSeoLocale(locale);

  const brand = resolveBrandName(product);
  const category = resolveLeafCategoryTitle(product);
  const lead =
    [brand, category].filter(Boolean).join(" ") ||
    truncateAtWord(cleanText(product?.title), TITLE_HEAD_MAX_LENGTH);

  const condition = resolveConditionLabel(product, seoLocale);
  const dimensions = formatDimensions(product);
  const price = formatProductPrice(product, seoLocale);
  const location = formatSellerLocation(product, seoLocale);
  const delivery = formatDeliveryOptions(product, seoLocale);

  const compose = (withDimensions: boolean, withDelivery: boolean) => {
    const firstSentence = [
      capitalizeFirst(lead),
      condition ? lowercaseFirst(condition) : undefined,
      withDimensions ? dimensions : undefined,
    ]
      .filter((part): part is string => !!part)
      .join(", ");

    const sentences = [
      firstSentence ? `${firstSentence}.` : undefined,
      price ? `${PRICE_LABEL[seoLocale]} ${price}.` : undefined,
      location
        ? SELLER_SENTENCE[seoLocale].replace("{city}", location)
        : undefined,
      withDelivery && delivery ? `${delivery}.` : undefined,
      TRUST_PHRASE[seoLocale],
    ];

    return sentences
      .filter((sentence): sentence is string => !!sentence)
      .join(" ");
  };

  const full = compose(true, true);

  if (full.length <= DESCRIPTION_MAX_LENGTH) {
    return full;
  }

  const withoutDimensions = compose(false, true);

  if (withoutDimensions.length <= DESCRIPTION_MAX_LENGTH) {
    return withoutDimensions;
  }

  return compose(false, false);
};
